import React, { useEffect, useRef, useState } from "react";
import { useBillingAuth } from "../billing/useBillingAuth";
import { isFirebaseConfigured } from "../billing/billingConfig";
import Link from "./Link";
import { ACCOUNT_NAV } from "../account/accountNav";
import { accountLabel, initialsFromUser } from "../account/userDisplay";
import { useOptionalAuth } from "../auth/AuthProvider";

const headerLoginClass =
  "inline-flex items-center justify-center rounded-full px-3.5 py-2 text-[13px] font-semibold text-ink ring-1 ring-ink/10 transition hover:bg-bg1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-60";

const mobileLoginClass =
  "inline-flex min-h-11 w-full items-center justify-center rounded-2xl px-4 text-sm font-semibold text-ink ring-1 ring-ink/10 hover:bg-bg1 disabled:opacity-60";

export default function HeaderAuthControls({ variant = "desktop", onAction }) {
  const { user, ready, signedIn } = useBillingAuth();
  const auth = useOptionalAuth();
  const configured = isFirebaseConfigured();
  const isMobile = variant === "mobile";
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function runSignOut() {
    setOpen(false);
    if (auth?.signOut) await auth.signOut();
    onAction?.();
  }

  if (!ready) {
    return (
      <div
        className={isMobile ? "h-11 w-full rounded-2xl bg-bg1" : "hidden h-9 w-[148px] rounded-full bg-bg1 md:block"}
        aria-hidden="true"
      />
    );
  }

  if (configured && signedIn) {
    if (isMobile) {
      return (
        <div className="flex w-full flex-col gap-1">
          <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">My account</p>
          {ACCOUNT_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex min-h-11 items-center rounded-2xl px-4 text-sm font-medium text-ink hover:bg-bg1"
              onClick={onAction}
            >
              {item.label}
            </Link>
          ))}
          <button type="button" onClick={runSignOut} className={mobileLoginClass}>
            Sign out
          </button>
        </div>
      );
    }

    return (
      <div className="relative hidden md:block" ref={wrapRef}>
        <button
          ref={buttonRef}
          type="button"
          className="inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-[13px] font-semibold text-ink ring-1 ring-ink/10 hover:bg-bg1"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-bg1 text-xs font-semibold">
            {initialsFromUser(user)}
          </span>
          <span className="max-w-[140px] truncate">{accountLabel(user)}</span>
        </button>
        <div
          role="menu"
          className={[
            "absolute right-0 top-[calc(100%+10px)] w-56 overflow-hidden rounded-2xl border border-ink/8 bg-white shadow-device transition",
            open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
          ].join(" ")}
        >
          <div className="p-1">
            {ACCOUNT_NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                role="menuitem"
                className="flex min-h-11 items-center rounded-xl px-3 text-sm text-ink hover:bg-bg1"
                onClick={() => {
                  setOpen(false);
                  onAction?.();
                }}
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              role="menuitem"
              onClick={runSignOut}
              className="flex min-h-11 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-ink hover:bg-bg1"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const loginClass = isMobile ? mobileLoginClass : headerLoginClass;

  return (
    <div className={isMobile ? "flex w-full flex-col gap-2" : "hidden md:block"}>
      <Link to="/sign-in" className={loginClass} onClick={onAction}>
        Sign in
      </Link>
    </div>
  );
}
